'use client';

import React, { ReactNode, useState, useRef, useEffect } from 'react';

import { cn } from '@/lib/utils/tailwind';

export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

interface TooltipProps {
  children: ReactNode;
  content: ReactNode;
  placement?: TooltipPlacement;
  showOnMobile?: boolean;
}

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  placement = 'top',
  showOnMobile = true,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle click outside to close tooltip
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node)
      ) {
        setIsVisible(false);
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isVisible]);

  const handleTouchStart = () => {
    if (showOnMobile) {
      setIsVisible(true);
      // Clear any existing timeout to prevent hiding if user taps multiple times
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }
  };

  const handleTouchEnd = () => {
    // Hide tooltip after a delay when touch ends
    if (showOnMobile) {
      timeoutRef.current = setTimeout(() => {
        setIsVisible(false);
      }, 1000);
    }
  };

  const basePosition =
    'absolute text-(--color-text-primary)! z-40 transition-opacity duration-300 pointer-events-none whitespace-nowrap text-xs text-white bg-gray-800 rounded px-2 py-1';

  const placementClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-1',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-4!',
    left: 'right-full top-1/2 -translate-y-1/2 mr-1',
    right: 'left-full top-1/2 -translate-y-1/2 ml-1',
  };

  const tooltipClasses = cn(
    basePosition,
    placementClasses[placement],
    'group-hover:opacity-100',
    {
      'opacity-100': isVisible,
      'opacity-0': !isVisible,
    },
  );

  return (
    <div
      className="relative group inline-block"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
      ref={tooltipRef}
    >
      {children}
      <div className={tooltipClasses}>{content}</div>
    </div>
  );
};
